import { lazy, Suspense, useCallback, useState } from "react";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "@mantine/core";
import { Tooltip } from "components/UI/Tooltip";
import { useUserTracks } from "stores/useUserTracks";
import { useUserWaypoints } from "stores/useUserWaypoints";
import { useTranslation } from "react-i18next";
import { CollapsibleTopMenuActionIcon } from "../CollapsibleActionIcon";
import { ImageTarget } from "hooks/useImageUpload";
import {
  showErrorNotification,
  showSuccessNotification,
} from "utils/notifications/customNotifications";

// The gallery and the upload dropzone are only needed after the gallery is opened
const LightboxGallery = lazy(() =>
  import("components/UI/Gallery/LightboxGallery").then((module) => ({
    default: module.LightboxGallery,
  }))
);
const UploadImageModal = lazy(() =>
  import("containers/UI/Modals/UploadImageModal").then((module) => ({
    default: module.UploadImageModal,
  }))
);

type GalleryIconProps = {
  disabled?: boolean;
  target?: ImageTarget;
};

export const GalleryIcon = ({ disabled = false, target }: GalleryIconProps) => {
  const { t } = useTranslation();
  const { selectedWaypoint } = useUserWaypoints();
  const { selectedTrack } = useUserTracks();
  const [open, setOpen] = useState(false);

  const targetEntity = target === "track" ? selectedTrack : selectedWaypoint;

  const imageLinks =
    targetEntity?.links && targetEntity?.links.length > 0
      ? targetEntity.links.filter(
          (link) =>
            link.includes("trackbook") &&
            new RegExp(/\.(png|jpe?g|gif)$/).test(link)
        )
      : [];

  const openGallery = useCallback(() => {
    // This also opens the upload modal in case there are no images - I'm respecting the original design
    setOpen(true);
  }, [setOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  return (
    <>
      <Tooltip label={t("editmode.open_gallery_button_tooltip")}>
        <div>
          <CollapsibleTopMenuActionIcon
            label={t("editmode.open_gallery_button_tooltip")}
            isOpen={target !== undefined}
            onClick={openGallery}
            disabled={disabled}
          >
            <FontAwesomeIcon icon={faImage} />
          </CollapsibleTopMenuActionIcon>
        </div>
      </Tooltip>
      <Suspense fallback={null}>
        {open ? (
          imageLinks.length > 0 ? (
            <LightboxGallery
              images={imageLinks}
              isOpen
              onClose={handleClose}
              target={target}
            />
          ) : (
            <Modal
              closeButtonProps={{ "aria-label": t("generic.close") }}
              centered
              opened
              onClose={handleClose}
              closeOnClickOutside
              title={t("lightbox_gallery_modal.title")}
              styles={{ title: { fontWeight: "bold", fontSize: "1.125rem" } }}
              style={{ zIndex: 400 }}
            >
              <UploadImageModal
                target={target}
                onClose={handleClose}
                onError={() => {
                  handleClose();
                  showErrorNotification({
                    message: t("image_upload_modal.upload_failure_text"),
                    title: t("generic.error_title"),
                  });
                }}
                onSuccess={() => {
                  handleClose();
                  showSuccessNotification({
                    message: t("image_upload_modal.upload_success_text"),
                    title: t("image_upload_modal.upload_success_title"),
                  });
                }}
              />
            </Modal>
          )
        ) : null}
      </Suspense>
    </>
  );
};
